import React, { useCallback, useState } from "react"
import { TagsInputDropdown } from "../AddData/TagsInputDropdown"
import { ModalPanel, Title, Button } from "dash-styled"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { FetchState } from "../../../../../../Models/Client/FetchState"

interface EditTagsModalProps {
    mode: "add" | "remove"
    affectedJobCount: number
    tagsFetch: FetchState<string[]>
    onSave: (value: string[]) => Promise<void>
    onClose: () => void
    onSearchTags: (keyword: string) => void
}

export function EditTagsModal({
    mode,
    affectedJobCount,
    tagsFetch,
    onSave,
    onClose,
    onSearchTags
}: EditTagsModalProps) {
    const [tags, setTags] = useState<string[]>([])
    const handleAddTag = useCallback(
        t => setTags(current => current.concat([t])),
        []
    )
    const handleRemoveTag = useCallback(
        t => setTags(current => current.filter(c => c !== t)),
        []
    )
    const [isProcessing, startProcessing, endProcessing] = useBoolean(false)
    const handleSave = useCallback(async () => {
        try {
            startProcessing()
            await onSave(tags)
        } finally {
            endProcessing()
        }
    }, [onSave, startProcessing, endProcessing, tags])
    const affectedJobCountText = `${affectedJobCount} data ${
        affectedJobCount > 1 ? "points" : "point"
    }`

    return (
        <ModalPanel
            testId={mode === "add" ? "add-tags-modal" : "remove-tags-modal"}
            onClose={onClose}
            className="max-w-2xl w-full"
        >
            <Title variant="heading2" weight="semibold" component="h2">
                {mode === "add"
                    ? `Add tags to ${affectedJobCountText}`
                    : `Remove tags from ${affectedJobCountText}`}
            </Title>
            <TagsInputDropdown
                allowAddNewTags={mode === "add"}
                value={tags}
                tagsFetch={tagsFetch}
                testId={mode === "add" ? "add-tags-input" : "remove-tags-input"}
                onAdd={handleAddTag}
                onRemove={handleRemoveTag}
                onSearch={onSearchTags}
            />
            <div className="flex justify-center pt-4">
                <Button
                    onClick={handleSave}
                    disabled={isProcessing || tags.length === 0}
                    className="w-auto"
                    testId={
                        mode === "add"
                            ? "add-tags-button"
                            : "remove-tags-button"
                    }
                >
                    {mode === "add"
                        ? isProcessing
                            ? "Adding..."
                            : tags.length === 1
                            ? `Add ${tags.length} tag`
                            : `Add ${tags.length || ""} tags`
                        : isProcessing
                        ? "Removing..."
                        : tags.length === 1
                        ? `Remove ${tags.length} tag`
                        : `Remove ${tags.length || ""} tags`}
                </Button>
            </div>
        </ModalPanel>
    )
}
